import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import StarIcon from '@mui/icons-material/Star';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDebouncedValue, useIntersection } from '@mantine/hooks';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchIcon from '@mui/icons-material/Search';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  Fade,
  FormControl,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Stack, SxProps, Theme } from '@mui/system';
import { LabeledItem } from './LabeledItem';
import { SiteTagItemMenu } from 'components/SiteTagItemMenu/SiteTagItemMenu';
import { useSiteTagDomains } from 'components/SiteTagItemMenu/hooks/useSiteTagDomains';
import {
  getSiteTagDomain,
  getSiteTagName,
} from 'components/SiteTagItemMenu/helpers/siteTagHelpers';
import {
  getAuthorPrimaryText,
  getAuthorSecondaryText,
  getAuthorAccountTypeChip,
} from './helpers/authorDisplayHelpers';
import { IntersectionLoader } from 'components/generic/IntersectionLoader';
import { PopperPropsExtended } from 'components/IconLabelFields/IconLabelTextField';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDataProvider } from 'react-admin';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { bloxValues } from 'themes/constants';
import { baseTransition, getSolidThemeGray } from 'utility/styles';
import {
  convertEditorTextToArray,
  formatUserToGenericItemLSS,
} from 'screens/editorial/asset/utility/getEditorConfigs';
import {
  BloxCustomDataProviderType,
  CommonQueryParamsType,
  GenericPagedApiResponse,
  UserAccountSummary,
} from '../../../utility/types/dataProvider';
import {
  KeywordListItem,
  SectionListItem,
  SiteTagDomainMap,
} from 'providers/contexts/extensions/SiteTaxonomy';
import { normalizeTestIdValue } from '../../../utility/functions';

//----------------------------------------------------------------------
// Types, Consts, Helper Functions
//----------------------------------------------------------------------

type GenericObject = Record<string, any>;

// Determine how to fetch/format or 'static' for passing in a fake list
type ValidListTargets = 'authors' | 'section' | 'keyword' | 'site_tags' | 'static';

export type GenericItemLSS = {
  path?: string; // path is usually the id / uuid, but not always, double check.
  id?: string | number;
  name?: string;
  label?: string;
  description?: string;
  value?: string; // generic
  [key: string]: any;
};

export type PossibleItemLSS = string | GenericItemLSS;

type GenericResultLSS = {
  data: {
    items: GenericItemLSS[];
    total: number;
    next?: string | number;
    prev?: string | number;
  };
  status: 'success';
};

type FetchItemsLSS =
  | UserAccountSummary
  | KeywordListItem
  | SectionListItem
  | GenericItemLSS;

type QueryResultLSS = GenericPagedApiResponse<FetchItemsLSS> | GenericResultLSS;

export type SelectionsLSS = PossibleItemLSS[];
export type SetSelectionsFnLSS = (selections: SelectionsLSS) => void;

/**
 * Helper to compare the value of the selected options...
 * we wont know what the "id" could be for comparing in LSS as its generic
 * and each editor that uses this is setup slightly differently.
 * Some items are a mix of strings and objects, eg. keywords/sections
 * others may have path prop (sections), and others could have an id prop.
 * Some need to be able to be created on the fly as strings too eg keywords.
 *
 * Unfortunatley that makes JSON.stringify not work as in some cases as
 * we need the ability to create a new keyword on demand if it doesn't exist,
 * so the plain string won't match the stringifed fetched keyword object. Arguably
 * you could transform the fetched data to also be just string when flattening based
 * on which listTarget is being used, but also wouldn't be a great solution as you
 * could lose other properties that are needed, eg. a uuid for a post request.
 *
 * Workaround: can take a look at Authors.tsx / initEditor.ts / CustomParagraph.ts
 * to see an example of storing API data / selection data on the tiptap tags
 * via a data-json attribute which allows you have one 'centralized' state
 * which also works with tiptaps collaborative editing features.
 */

function getComparisonId(item: PossibleItemLSS, listTarget: string): string {
  // If it's a string or number, use it directly
  if (typeof item === 'string' || typeof item === 'number') {
    return String(item);
  }

  // Access path property for keywords / section compare
  if (
    listTarget === 'keyword' ||
    listTarget === 'section' ||
    listTarget === 'site_tags'
  ) {
    if (item?.path) {
      return String(item?.path);
    }
  }

  // Default fallback to id property (eg. static, flags, generic)
  if (item?.id) {
    return String(item?.id);
  }

  console.warn('[getComparisonValue] Unable to determine id for item:', item);
  return '';
}

// Determine the displayed text for each option
function getLabelText(selection: PossibleItemLSS): string {
  if (typeof selection === 'object') {
    return selection?.label || selection?.path || selection?.name || '';
  }
  if (typeof selection === 'string') {
    return String(selection); // Directly display a string if it's not an object
  }
  return '';
}

// Determine tooltip titles
function getTitleText(selection: PossibleItemLSS) {
  if (typeof selection === 'object') {
    return selection?.description || '';
  }
  if (typeof selection === 'string') {
    return selection; // Directly display a string if it's not an object
  }
  return '';
}

//----------------------------------------------------------------------
// Subcomponents
//----------------------------------------------------------------------

interface LSSDragItemProps {
  item: PossibleItemLSS;
  listTarget: string;
  onRemove: (item: PossibleItemLSS) => void;
  onSendToTop?: (item: PossibleItemLSS) => void;
  isDataPrivate?: boolean;
  isPrimary?: boolean;
  disabled?: boolean;
  primaryTooltipText?: string;
}

const LSSDragItem = memo(
  ({
    item,
    listTarget,
    onRemove,
    onSendToTop,
    isDataPrivate,
    isPrimary,
    disabled,
    primaryTooltipText,
  }: LSSDragItemProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
      useSortable({ id: getComparisonId(item, listTarget) });
    const getTitle = getTitleText(item);
    const getLabel = getLabelText(item);

    const handleRemove = useCallback(() => {
      onRemove(item);
    }, [onRemove, item]);

    // Send to Top menu functions/state
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(menuAnchorEl);

    const handleMenuOpen = useCallback(
      (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLButtonElement>) => {
        event?.stopPropagation();
        setMenuAnchorEl(event?.currentTarget);
      },
      [setMenuAnchorEl],
    );

    const handleMenuClose = useCallback(() => {
      setMenuAnchorEl(null);
    }, [setMenuAnchorEl]);

    const handleSendToTop = useCallback(() => {
      if (onSendToTop) {
        onSendToTop(item);
      }
      handleMenuClose();
    }, [onSendToTop, item, handleMenuClose]);

    const handleKeyUp = useCallback(
      (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (
          event.key === ' ' ||
          event.key === 'Spacebar' ||
          event.key === 'Space' ||
          event.key === 'Enter'
        ) {
          // Space (and enter) on/off the dnd reorder mode
          event?.preventDefault();
        } else if (event.key === 'ContextMenu' || event.key === 'Alt') {
          // ContextMenu or Alt will open the menu (to not conflict with MUI defaults during dnd reorder)
          event?.preventDefault();
          handleMenuOpen(event);
        }
      },
      [handleMenuOpen],
    );

    const dndLiSx: SxProps<Theme> = {
      transform: CSS.Transform.toString(transform),
      transition: transition || baseTransition,
      opacity: isDragging ? 0.25 : 1,
      py: 0.5,
      px: 1,
      display: 'grid',
      gridTemplateColumns: 'auto 1fr auto',
      gridTemplateAreas: `'start text end'`,
      alignItems: 'center',
      gap: 1,
      cursor: isDragging ? 'grabbing' : 'grab',
    };

    return (
      <ListItem
        ref={setNodeRef}
        dense={true}
        title={getTitle}
        data-testid={`lss-drag-item-${normalizeTestIdValue(getComparisonId(item, listTarget))}`}
        data-private={isDataPrivate}
        sx={dndLiSx}
        {...attributes}
        // No drag listeners if minimenu is open
        {...(isMenuOpen ? {} : listeners)}
      >
        <Menu
          id="send-to-top-menu"
          data-testid="send-to-top-menu"
          anchorEl={menuAnchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          sx={{
            '& .MuiPaper-root': {
              borderRadius: 1,
              width: 150,
              boxShadow: theme.shadows[3],
            },
            '& .MuiList-root': {
              padding: 0,
            },
            '& .MuiMenuItem-root': {
              '& .MuiTypography-root': {
                fontSize: bloxValues.smallFontSizeRem,
              },
              '&:hover': {
                bgcolor: theme.palette.action.selected,
              },
            },
          }}
          slotProps={{
            list: { 'aria-labelledby': 'send-to-top-menu' },
          }}
        >
          <MenuItem onClick={handleSendToTop}>
            <ListItemText primary={t('blox.general.send_to_top')} />
          </MenuItem>
        </Menu>

        <IconButton
          data-testid={`lss-drag-menu-${normalizeTestIdValue(getComparisonId(item, listTarget))}`}
          aria-label="draghandle-menu"
          disabled={isDragging || disabled}
          title={t('blox.general.menu_of', { label: getLabel })}
          onKeyUp={handleKeyUp}
          onClick={handleMenuOpen}
          sx={{
            gridArea: 'start',
            minWidth: 'auto',
            color: theme.palette.action.disabled,
            fontSize: bloxValues.iconSizeSmall,
            '& svg': {
              fontSize: bloxValues.iconSizeSmall,
            },
          }}
        >
          <DragHandleIcon />
        </IconButton>

        <Box
          sx={{
            gridArea: 'text',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
            gap: 0.5,
          }}
        >
          {!isDragging && isPrimary && (
            <Tooltip
              title={primaryTooltipText}
              disableInteractive
              arrow
            >
              <StarIcon
                sx={{
                  display: 'block',
                  borderRadius: 1,
                  bgcolor: theme.palette.primary.main,
                  p: '2px',
                  fontSize: bloxValues.iconSizeSmall,
                  color: theme.palette.background.paper,
                }}
              />
            </Tooltip>
          )}

          <Typography
            sx={{
              wordBreak: 'break-word',
              fontSize: bloxValues.smallFontSizeRem,
              fontWeight: isPrimary
                ? bloxValues.fontWeightMedium
                : bloxValues.fontWeightRegular,
            }}
          >
            {getLabel}
          </Typography>
        </Box>

        <IconButton
          data-testid={`lss-remove-selection-${normalizeTestIdValue(getComparisonId(item, listTarget))}`}
          aria-label="remove"
          disabled={isDragging || disabled}
          title={t('blox.general.remove_item', { label: getLabel })}
          onClick={handleRemove}
          sx={{
            gridArea: 'end',
            color: theme.palette.action.disabled,
            fontSize: bloxValues.iconSizeSmall,
            '& svg': {
              fontSize: bloxValues.iconSizeSmall,
            },
            '&:hover': {
              color: theme.palette.error.main,
            },
          }}
        >
          <HighlightOffIcon />
        </IconButton>
      </ListItem>
    );
  },
);
LSSDragItem.displayName = 'LSSDragItem';

interface LSSChipProps {
  item: PossibleItemLSS;
  listTarget: string;
  onRemove: (item: PossibleItemLSS) => void;
  isDataPrivate?: boolean;
  disabled?: boolean;
}

const LSSChip = memo(
  ({ item, onRemove, listTarget, isDataPrivate, disabled }: LSSChipProps) => {
    const theme = useTheme();
    const getTitle = getTitleText(item);
    const getLabel = getLabelText(item);

    return (
      <Chip
        data-testid={`lss-chip-${normalizeTestIdValue(getComparisonId(item, listTarget))}`}
        size="small"
        title={getTitle}
        label={getLabel}
        disabled={disabled}
        data-private={isDataPrivate}
        onDelete={() => onRemove(item)}
        sx={{
          height: 'auto',
          maxWidth: '100%',
          p: 0.25,
          '& .MuiChip-label': {
            maxWidth: '100%',
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            fontSize: bloxValues.smallFontSizeRem,
          },
          '& .MuiChip-deleteIcon': {
            '&:hover': {
              color: theme.palette.error.main,
            },
          },
        }}
      />
    );
  },
);
LSSChip.displayName = 'LSSChip';

//----------------------------------------------------------------------
// Main Component
//----------------------------------------------------------------------

export type FilterOption = {
  value: string;
  label: string;
  paramKey?: string; // The key to use in optionalParams
  paramValue?: string; // The value to set for paramKey
};

export type FilterConfig = {
  options: FilterOption[];
  defaultValue?: string;
};

type OptionalParams = {
  account_type?: string; // authors fetch
  [key: string]: any; // any other params
};

export type LSSProps = {
  label: string; // Needed for query key as well in case of 'static'.
  listTarget: ValidListTargets;
  isFocused?: boolean;
  selections?: SelectionsLSS;
  dndReorder?: boolean;
  setSelections?: SetSelectionsFnLSS;
  editorState?: GenericObject | undefined;
  customList?: GenericItemLSS[];
  allowCustom?: boolean;
  clearSelectionsFlag?: boolean;
  isDataPrivate?: boolean;
  sx?: SxProps<Theme>;
  helpText?: string;
  disabled?: boolean;
  optionalParams?: OptionalParams;
  filterConfig?: FilterConfig;
  authorDisplay?: boolean; // Display for authors with account type chip and name/email
  primaryTooltipText?: string; // Tooltip text for the primary indicator
  showSearchInput?: boolean;
  showResultsList?: boolean;
  originDomain?: string; // In site_tags mode, marks the matching tag with an "Origin" badge
  onSetOriginDomain?: (domain: string) => void; // Callback for site_tags mode, updates origin_domain in the form
  siteTagDomainMap?: SiteTagDomainMap; // In site_tags mode, domain map keyed by tag name, managed by the parent
  onSiteTagDomainsLoaded?: (map: SiteTagDomainMap) => void; // site_tags mode, called when postSiteTagDomains returns so the parent can persist the map across dialog open/close cycles.
  assetUuid?: string; // In site_tags mode, UUID of the asset for "View at site" menu item
  disableOriginActions?: boolean; // site_tags mode, Syndicated assets (has origin_sites) cannot have origin_domain changed.
};

export const ListSearchSelect = memo(
  ({
    listTarget,
    isFocused,
    disabled,
    customList,
    label,
    editorState,
    dndReorder = false,
    allowCustom = false,
    selections,
    setSelections,
    clearSelectionsFlag,
    isDataPrivate,
    sx,
    helpText,
    optionalParams = {},
    filterConfig,
    authorDisplay = false,
    primaryTooltipText,
    showSearchInput = true,
    showResultsList = true,
    originDomain,
    onSetOriginDomain,
    siteTagDomainMap = {},
    onSiteTagDomainsLoaded,
    assetUuid,
    disableOriginActions,
  }: LSSProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dataProvider = useDataProvider<BloxCustomDataProviderType>();
    const [currentSelections, setCurrentSelections] = useState<PossibleItemLSS[]>([]);
    const [activeFilterValue, setActiveFilterValue] = useState<string>(
      filterConfig?.defaultValue || filterConfig?.options?.[0]?.value || '',
    );

    // Initialize query params with default filter applied
    const initialQueryParams = useMemo(() => {
      let params: CommonQueryParamsType = { query: '', limit: 50, ...optionalParams };
      if (filterConfig?.defaultValue) {
        const selectedOption = filterConfig.options?.find(
          (opt) => opt.value === filterConfig.defaultValue,
        );
        if (selectedOption?.paramKey) {
          const paramKey = selectedOption.paramKey;
          if (selectedOption.paramValue !== undefined) {
            params[paramKey] = selectedOption.paramValue;
          } else {
            delete params[paramKey];
          }
        }
      }
      return params;
    }, [filterConfig, optionalParams]);

    const [optionListQueryParams, setOptionListQueryParams] =
      useState<CommonQueryParamsType>(initialQueryParams);
    const [debouncedOptionListQuery] = useDebouncedValue(optionListQueryParams, 300);
    const inputElementRef = useRef<HTMLInputElement | null>(null);
    const [inputValue, setInputValue] = useState('');
    const inputHasText = Boolean(inputValue);
    const passedSx = useMemo(() => {
      const resolveSx = typeof sx === 'function' ? sx(theme) : sx;
      return resolveSx;
    }, [sx, theme]);

    // Default primary tooltip text
    const translatedPrimaryTooltipText =
      primaryTooltipText || t('blox.general.primary', 'Primary');

    // Intersection observer load more on infinite scroll
    const { ref: ixRef, entry: inView } = useIntersection({
      root: null,
      threshold: 0.5,
    });

    // Drag and drop reorder
    const sensors = useSensors(
      useSensor(MouseSensor, {
        activationConstraint: { distance: 0.1 },
      }),
      useSensor(TouchSensor, {
        activationConstraint: { distance: 0.1 },
      }),
      useSensor(KeyboardSensor),
    );

    // useInfiniteQuery for fetching the list
    const infListQuery = useInfiniteQuery<
      QueryResultLSS,
      Error,
      InfiniteData<QueryResultLSS>,
      (string | CommonQueryParamsType)[],
      string | number
    >({
      queryKey: ['infiniteOptionListQuery', debouncedOptionListQuery, listTarget, label],
      enabled: showResultsList,
      queryFn: async ({ pageParam }): Promise<QueryResultLSS> => {
        if (listTarget === 'static') {
          // Filter the static list based on the input
          // value searching only in path and label
          const filteredList = (customList || []).filter(
            (item: GenericItemLSS) =>
              item?.path?.toLowerCase().startsWith(inputValue.toLowerCase()) ||
              (item.label &&
                item.label.toLowerCase().startsWith(inputValue.toLowerCase())),
          );

          // Pretend paged query response for static lists
          return {
            data: {
              items: filteredList,
              total: filteredList.length,
              next: undefined,
              prev: undefined,
            },
            status: 'success',
          };
        }

        const paramsWithPage = {
          ...debouncedOptionListQuery,
          query: debouncedOptionListQuery?.query?.trim() || '',
          page: pageParam as string,
        };
        try {
          switch (listTarget) {
            case 'section': {
              const result = await dataProvider.getSectionList(paramsWithPage);
              return result;
            }
            case 'keyword': {
              const result = await dataProvider.getKeywordList(paramsWithPage);
              return result;
            }
            case 'authors': {
              const result = await dataProvider.getUserAccountSearch(paramsWithPage);
              return result;
            }
            case 'site_tags': {
              const result = await dataProvider.getSiteTagList(paramsWithPage);
              return result;
            }
            default: {
              throw new Error(`[ListSearchSelect] Invalid listTarget: ${listTarget}`);
            }
          }
        } catch (error) {
          throw new Error(`[LSS Query] error - ${JSON.stringify(error, null, 2)}`);
        }
      },
      initialPageParam: '',
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      getNextPageParam: (response) => {
        return response?.data?.next || undefined;
      },
      getPreviousPageParam: (response) => {
        return response?.data?.prev || undefined;
      },
    });

    // Flatten pages into a single array
    const flatItems = useMemo<PossibleItemLSS[]>(() => {
      switch (listTarget) {
        case 'static': {
          return (
            customList?.filter(
              (item) =>
                item?.path?.toLowerCase().startsWith(inputValue.toLowerCase()) ||
                (item.label &&
                  item.label.toLowerCase().startsWith(inputValue.toLowerCase())),
            ) || []
          );
        }

        case 'authors': {
          const authorsData =
            infListQuery.data?.pages.flatMap(
              (page) => page?.data?.items as UserAccountSummary[],
            ) || [];
          // Format the authors data returned into custom lss items
          const customListItems = authorsData.map(formatUserToGenericItemLSS);
          return customListItems;
        }

        default: {
          return (
            infListQuery.data?.pages.flatMap(
              (page) => page?.data?.items as GenericItemLSS[],
            ) || []
          );
        }
      }
    }, [listTarget, customList, inputValue, infListQuery]);

    // dnd needs stable ids for reordering
    // todo: maybe add directly to the state/flatitems instead?
    const dndIds = useMemo(() => {
      if (!dndReorder) {
        return []; // no need if dnd disabled
      }
      return currentSelections.map((selection) => getComparisonId(selection, listTarget));
    }, [currentSelections, dndReorder, listTarget]);

    // Centralized helper fn to update both
    // currentSelections and parentSelections
    const updateSelections = useCallback(
      (newSelections: PossibleItemLSS[]) => {
        // Update the local state
        setCurrentSelections(newSelections);
        // If setSelections callback was passed also call
        // it with the new selections directly
        if (setSelections) {
          setSelections(newSelections);
        }
      },
      [setSelections],
    );

    const clearSelections = useCallback(() => {
      updateSelections([]);
    }, [updateSelections]);

    const handleSearchInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      setOptionListQueryParams((prevParams) => ({
        ...prevParams,
        query: e.target.value,
      }));
    }, []);

    // For clear button
    const clearInputAndQuery = useCallback(() => {
      setInputValue('');
      setOptionListQueryParams((prevParams) => ({
        ...prevParams,
        query: '',
      }));
    }, []);

    // Handle options filter change
    const handleFilterChange = useCallback(
      (event: SelectChangeEvent<string>) => {
        const newValue = event.target.value;
        setActiveFilterValue(newValue);

        // Find the selected filter option
        const selectedOption = filterConfig?.options?.find(
          (opt) => opt.value === newValue,
        );

        // Update query params based on filter selection
        setOptionListQueryParams((prevParams) => {
          const updatedParams = { ...prevParams };

          if (selectedOption?.paramKey) {
            if (selectedOption?.paramValue !== undefined) {
              // Set the param key to the specified value
              updatedParams[selectedOption.paramKey] = selectedOption.paramValue;
            } else {
              // Remove the param key if no value specified (undefined)
              delete updatedParams[selectedOption.paramKey];
            }
          }

          return updatedParams;
        });
      },
      [filterConfig],
    );

    const handleOptionSelect = useCallback(
      (selectedOption: PossibleItemLSS) => {
        const selectionId = getComparisonId(selectedOption, listTarget);
        if (!selectionId) {
          return;
        }

        // Check if the current selections include the selected option
        const isSelected = currentSelections.some((option) => {
          const optionValue = getComparisonId(option, listTarget);
          const comparison = optionValue === selectionId;
          return comparison;
        });

        // Remove the option if it's already selected
        // Add the option if it wasn't already selected
        if (isSelected) {
          const removedSelection = currentSelections.filter(
            (option) => getComparisonId(option, listTarget) !== selectionId,
          );
          updateSelections(removedSelection);
        } else {
          const addedSelection = [...currentSelections, selectedOption];
          updateSelections(addedSelection);
        }
      },
      [currentSelections, updateSelections, listTarget],
    );

    // Handle keydown event for input box
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && inputValue.trim() !== '') {
          e.preventDefault(); // Prevent form submission if the user hits enter

          const trimmedInput = inputValue.trim();

          // Determine if the input value is already selected
          const isAlreadySelected = currentSelections.some(
            (selection: PossibleItemLSS) => {
              const selectionValue = getComparisonId(selection, listTarget);
              return selectionValue === trimmedInput;
            },
          );

          const itemIsInOptionData = flatItems?.some((item: PossibleItemLSS) => {
            const optionId = getComparisonId(item, listTarget);
            return optionId === trimmedInput;
          });

          // Add the string to the selections array
          // if not already selected and available, or allowCustom is true
          if (!isAlreadySelected && (allowCustom || itemIsInOptionData)) {
            handleOptionSelect(trimmedInput); // Make sure handleOptionSelect handles strings properly

            // Clear input value to reset custom options
            if (listTarget === 'static') {
              setInputValue('');
            }

            // Reset the query
            setOptionListQueryParams((prevParams) => ({
              ...prevParams,
              query: '',
            }));
          } else if (isAlreadySelected) {
            // Clear the input field when the item is already selected
            if (listTarget === 'static') {
              setInputValue('');
            }

            // Reset the query
            setOptionListQueryParams((prevParams) => ({
              ...prevParams,
              query: '',
            }));
          }
        }
      },
      [
        allowCustom,
        currentSelections,
        handleOptionSelect,
        inputValue,
        listTarget,
        flatItems,
      ],
    );

    // Remove a single selection via the chip set
    const handleDeleteSelection = useCallback(
      (selection: PossibleItemLSS) => {
        const selectionId = getComparisonId(selection, listTarget);
        if (!selectionId) {
          return;
        }

        // Filter out the selection using the determined comparison value
        const newSelections = currentSelections.filter((option: PossibleItemLSS) => {
          const optionId = getComparisonId(option, listTarget);
          const isOptionSelected = optionId !== selectionId;
          return isOptionSelected;
        });

        updateSelections(newSelections);
      },
      [currentSelections, updateSelections, listTarget],
    );

    // Handle drag end event for reordering
    const handleDragEnd = useCallback(
      (event: DragEndEvent) => {
        if (!dndReorder) {
          return;
        }

        const { active, over } = event;
        if (!over || active?.id === over?.id) return;

        const oldIndex = currentSelections.findIndex(
          (s) => getComparisonId(s, listTarget) === active?.id,
        );
        const newIndex = currentSelections.findIndex(
          (s) => getComparisonId(s, listTarget) === over?.id,
        );

        if (oldIndex !== -1 && newIndex !== -1) {
          const newSelections = arrayMove(currentSelections, oldIndex, newIndex);
          updateSelections(newSelections);
        }
      },
      [currentSelections, updateSelections, dndReorder, listTarget],
    );

    const handleSendToTop = useCallback(
      (item: PossibleItemLSS) => {
        if (!dndReorder) {
          return; // Only allow send to top if dnd reorder is enabled
        }

        // Get the id of the item to send to top
        const selectionId = getComparisonId(item, listTarget);
        if (!selectionId) {
          return;
        }

        // Filter instance of the item from the current selections
        const filteredSelections = currentSelections.filter(
          (option) => getComparisonId(option, listTarget) !== selectionId,
        );

        // Move to the beginning of the list.
        const newSelections = [item, ...filteredSelections];

        // Update states
        updateSelections(newSelections);
      },
      [currentSelections, updateSelections, dndReorder, listTarget],
    );

    const totalResults = useMemo(() => {
      if (listTarget === 'static') {
        return flatItems?.length;
      } else {
        return infListQuery?.data?.pages?.[0]?.data?.total;
      }
    }, [listTarget, flatItems?.length, infListQuery?.data]);

    // If the parent component calls for clear, clear internally
    useEffect(() => {
      if (clearSelectionsFlag) {
        clearSelections(); // Clear selections in ListSearchSelect
      }
    }, [clearSelectionsFlag, clearSelections]);

    useSiteTagDomains({
      dataProvider,
      isFocused,
      isListReady: infListQuery.isSuccess,
      listTarget,
      onDomainsLoaded: onSiteTagDomainsLoaded,
      items: flatItems,
    });

    // Sync / update selections based on conditions
    useEffect(() => {
      if (selections !== undefined) {
        // Use selections if directly passed as prop
        updateSelections(selections);
      } else {
        // Use selections from editorState if none are passed (CustomParagraph.ts)
        const selectionsArray = convertEditorTextToArray(editorState?.text || '');
        updateSelections(selectionsArray);
      }
    }, [editorState?.text, updateSelections, selections]);

    // UI Consts
    const selectedCountStr = currentSelections?.length
      ? `(${currentSelections.length})`
      : '';
    const searchOrAdd = allowCustom
      ? t('blox.general.search_or_add')
      : t('blox.general.search');
    const inputPlaceholder = `${searchOrAdd}${label ? ' ' + label.toLowerCase() : ''}...`;
    const isDataPrivateProp = isDataPrivate ? true : undefined;

    const endOfResultsMsg = flatItems?.length
      ? t('blox.general.showing_total_results', { total: totalResults })
      : allowCustom
        ? t('blox.general.no_results_press_enter_to_add')
        : t('blox.general.no_results_found');

    return (
      <Box
        id="lss-grid-container"
        data-testid={`lss-root-${listTarget}`}
        sx={{
          display: 'grid',
          gridTemplateRows: 'auto 1fr',
          gridTemplateAreas: `'lss-top' 'lss-list'`,
          height: '100%',
          maxHeight: '100%',
          overflow: 'hidden',
          ...passedSx,
        }}
      >
        <Box
          id="lss-top"
          data-testid={`lss-top-${listTarget}`}
          sx={{
            gridArea: 'lss-top',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            py: '10px',
            mb: '5px',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: `1px solid ${theme.palette.action.selected}`,
          }}
        >
          {showSearchInput && (
            <>
              <TextField
                data-testid={`lss-search-input-${listTarget}`}
                inputRef={inputElementRef}
                value={optionListQueryParams.query}
                onChange={handleSearchInput}
                onKeyDown={handleKeyDown}
                placeholder={inputPlaceholder}
                data-private={isDataPrivateProp}
                fullWidth={true}
                variant="outlined"
                type="text"
                size="small"
                margin="dense"
                color="primary"
                autoComplete="off"
                sx={{ my: 0.5 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{
                          marginRight: '8px',
                        }}
                      >
                        <SearchIcon
                          sx={{
                            fontSize: '1.5rem',
                          }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <Fade
                        in={inputHasText}
                        timeout={500}
                      >
                        <InputAdornment
                          position="end"
                          sx={{
                            marginLeft: '8px',
                          }}
                        >
                          {inputHasText && (
                            <Tooltip
                              title={t('blox.actions.clear')}
                              disableInteractive
                            >
                              <IconButton
                                data-testid={`lss-search-clear-${listTarget}`}
                                aria-label="clear"
                                onClick={clearInputAndQuery}
                                size="small"
                              >
                                <ClearRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </InputAdornment>
                      </Fade>
                    ),
                  },

                  inputLabel: { shrink: true },
                }}
              />

              {filterConfig && filterConfig.options.length > 0 && (
                <FormControl
                  data-testid={`lss-filter-${listTarget}`}
                  fullWidth
                  size="small"
                >
                  <Select
                    value={activeFilterValue}
                    onChange={handleFilterChange}
                    disabled={disabled}
                    variant="outlined"
                    sx={{
                      fontSize: bloxValues.tinyFontSizeRem,
                    }}
                  >
                    {filterConfig.options.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        sx={{
                          fontSize: bloxValues.tinyFontSizeRem,
                        }}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}

          <Accordion
            data-testid={`lss-selected-accordion-${listTarget}`}
            disableGutters={true}
            defaultExpanded={false}
            square={true}
            elevation={0}
            slotProps={{
              transition: {
                timeout: 0,
              },
            }}
            sx={{
              width: '100%',
              // remove mui accordion border
              '::before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              data-testid={`lss-selected-summary-${listTarget}`}
              expandIcon={<ExpandMoreIcon />}
              sx={{
                py: 0.5,
                px: 1,
                borderRadius: '4px',
                minHeight: 'auto',
                backgroundColor: getSolidThemeGray(theme),

                '&:hover': {
                  backgroundColor: theme.palette.action.selected,
                },

                borderBottom: '1px solid transparent',
                '&.Mui-expanded': {
                  borderBottom: `1px solid ${theme.palette.action.hover}`,
                  borderTopLeftRadius: '4px',
                  borderTopRightRadius: '4px',
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                },

                '& .MuiAccordionSummary-content': {
                  margin: 0,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                },
              }}
            >
              <Stack
                id="selected-count-help-text"
                data-testid={`lss-selected-summary-text-${listTarget}`}
                sx={{ gap: 0.5 }}
              >
                <Typography
                  sx={{
                    fontSize: bloxValues.tinyFontSizeRem,
                    color: theme.palette.text.secondary,
                    textTransform: 'capitalize',
                  }}
                >
                  {t('blox.general.selected', 'Selected')} {selectedCountStr}
                </Typography>

                {helpText && (
                  <Typography
                    sx={{
                      fontSize: bloxValues.tinyFontSizeRem,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {helpText}
                  </Typography>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails
              data-testid={`lss-selected-details-${listTarget}`}
              sx={{
                p: 0,
                borderBottomLeftRadius: '4px',
                borderBottomRightRadius: '4px',
                minHeight: '32px',
                maxHeight: '28vh',
                overflow: 'auto',
                display: 'flex',
                flexWrap: 'nowrap',
                flexDirection: 'column',
                backgroundColor: getSolidThemeGray(theme),
                boxShadow: `0px 6px 4px -6px ${theme.palette.action.disabled}`,
              }}
            >
              {dndReorder ? (
                <DndContext
                  sensors={sensors}
                  onDragEnd={handleDragEnd}
                  autoScroll={true}
                  modifiers={[restrictToParentElement]}
                >
                  {currentSelections.length > 1 && (
                    <Typography
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        py: 0.5,
                        px: 1,
                        fontSize: bloxValues.microFontSizeRem,
                        color: theme.palette.text.disabled,
                      }}
                    >
                      {t(
                        'blox.editorial.asset.sections.help_drag_drop',
                        'Drag and drop items to reorder.',
                      )}
                    </Typography>
                  )}
                  <SortableContext items={dndIds}>
                    <List
                      id="lss-sort-list"
                      data-testid={`lss-sort-list-${listTarget}`}
                      dense={true}
                      disablePadding={true}
                    >
                      {currentSelections.map((selection: PossibleItemLSS, index) => {
                        const isPrimary = Boolean(index === 0);
                        const getKey = JSON.stringify(selection) + '-LSSDragItem';
                        return (
                          <LSSDragItem
                            key={getKey}
                            item={selection}
                            listTarget={listTarget}
                            isPrimary={isPrimary}
                            isDataPrivate={isDataPrivate}
                            onRemove={handleDeleteSelection}
                            onSendToTop={handleSendToTop}
                            disabled={disabled}
                            primaryTooltipText={translatedPrimaryTooltipText}
                          />
                        );
                      })}
                    </List>
                  </SortableContext>
                </DndContext>
              ) : (
                <Box
                  id="lss-chip-box"
                  data-testid={`lss-chip-box-${listTarget}`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: 1,
                    p: 1,
                  }}
                >
                  {currentSelections.map((selection: PossibleItemLSS) => {
                    const getKey = JSON.stringify(selection) + '-LSSChip';
                    return (
                      <LSSChip
                        key={getKey}
                        item={selection}
                        listTarget={listTarget}
                        isDataPrivate={isDataPrivate}
                        onRemove={handleDeleteSelection}
                        disabled={disabled}
                      />
                    );
                  })}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
        {showResultsList && (
          <Box
            id="lss-list"
            data-testid={`lss-results-${listTarget}`}
            sx={{
              gridArea: 'lss-list',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              overflow: 'auto',
            }}
          >
            <List
              data-testid={`lss-results-list-${listTarget}`}
              dense={true}
              disablePadding={true}
            >
              {flatItems.map((option: PossibleItemLSS) => {
                const comparisonValue = getComparisonId(option, listTarget);

                // If falsy skip rendering item since we can't execute selection logic.
                if (!comparisonValue) {
                  return null;
                }

                const isSelected = currentSelections.some((selection) => {
                  const selectionValue = getComparisonId(selection, listTarget);
                  return selectionValue === comparisonValue;
                });

                const itemKey = JSON.stringify(option) + '-menu-item';
                const itemLabelText = getLabelText(option);

                // Site-tag: origin badge & domain menu
                const siteTagName =
                  listTarget === 'site_tags' ? getSiteTagName(option) : '';
                const siteTagDomain = getSiteTagDomain(siteTagName, siteTagDomainMap);

                // If its an object, waterfall down desc, path, or name -- else if its a string use the string
                const itemTooltipTxt =
                  typeof option === 'object'
                    ? option?.description || option?.path || option?.name || ''
                    : String(option);

                // Show tooltip if its not the same as what the label is
                const showTooltip =
                  itemLabelText !== itemTooltipTxt || itemTooltipTxt.length > 50;

                return (
                  <ListItemButton
                    key={itemKey}
                    data-testid={`lss-result-item-${listTarget}-${normalizeTestIdValue(comparisonValue)}`}
                    data-private={isDataPrivateProp}
                    dense={true}
                    disableRipple={true}
                    disableGutters={true}
                    tabIndex={-1}
                    onClick={(e) => handleOptionSelect(option)}
                    selected={isSelected}
                    disabled={disabled}
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                      fontSize: bloxValues.regularFontSizeRem,
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      disabled={disabled}
                      sx={{
                        fontSize: bloxValues.iconSizeSmall,
                        '& svg': {
                          fontSize: bloxValues.iconSizeSmall,
                        },
                      }}
                      slotProps={{
                        input: { 'aria-label': `checkbox-${itemLabelText}` },
                      }}
                    />
                    <Tooltip
                      disableInteractive={true}
                      arrow={true}
                      enterDelay={500}
                      leaveDelay={0}
                      placement="bottom"
                      title={showTooltip ? itemTooltipTxt : undefined}
                      slotProps={{
                        popper: {
                          'data-private': isDataPrivateProp,
                        } as PopperPropsExtended,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {authorDisplay ? (
                          // Author display
                          <LabeledItem
                            primaryText={getAuthorPrimaryText(option)}
                            secondaryText={getAuthorSecondaryText(option)}
                            chip={getAuthorAccountTypeChip(option)}
                            isDataPrivate={isDataPrivateProp}
                            size="regular"
                          />
                        ) : (
                          // Standard display
                          <Typography
                            data-private={isDataPrivateProp}
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%',
                              fontSize: bloxValues.regularFontSizeRem,
                            }}
                          >
                            {itemLabelText}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                    {listTarget === 'site_tags' && (
                      <SiteTagItemMenu
                        domain={siteTagDomain}
                        originDomain={originDomain}
                        onSetOriginDomain={onSetOriginDomain}
                        assetUuid={assetUuid}
                        disabled={disabled}
                        disableOriginActions={disableOriginActions}
                        siteTagName={siteTagName}
                      />
                    )}
                  </ListItemButton>
                );
              })}
              <ListItem
                key="lss-intersection-loader"
                data-testid={`lss-results-loader-${listTarget}`}
                dense={true}
                disableGutters={true}
                tabIndex={-1}
                sx={{ p: 1 }}
              >
                <IntersectionLoader
                  compact={true}
                  hasNextPage={infListQuery.hasNextPage}
                  isFetching={infListQuery.isFetching}
                  fetchNextPage={infListQuery.fetchNextPage}
                  ixRef={ixRef}
                  entry={inView}
                  endOfResultsMessage={endOfResultsMsg}
                />
              </ListItem>
            </List>
          </Box>
        )}
      </Box>
    );
  },
);

ListSearchSelect.displayName = 'ListSearchSelect';
